import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { API_ROUTES } from '../../util/route.constants';
import { Observable, map, of, tap } from 'rxjs';
import { LookupEntryInterface, MSCognitiveConfigInterface } from '../../models/lookup.interface';
import { HttpClient } from '@angular/common/http';
import { SpeechToTextService } from '@privatelibdev/speech-to-text';
import { hasAuthTokens } from '../../util/auth-tokens.util';

@Injectable({
  providedIn: 'root'
})
export class LookupService {
  private readonly BASE_URL = environment.cva_url + API_ROUTES.LOOKUP;
  private cognitiveServicesConfig: MSCognitiveConfigInterface | undefined;
  private isSpeechServiceInitiated = false;

  private cacheLookupMapper: Partial<Record<string, LookupEntryInterface[]>> = {};

  constructor(
    private readonly http: HttpClient
  ) { }

  getLookupValuesByType(lookupType: string): Observable<LookupEntryInterface[]> {
    if (!hasAuthTokens()) {
      return of([]);
    }

    if (this.cacheLookupMapper[lookupType]) {
      return of(this.cacheLookupMapper[lookupType]  ?? []);
    }

    return this.http.get<{ response: LookupEntryInterface[] }>(this.BASE_URL + "/" + lookupType).pipe(
      map(response => {
        this.cacheLookupMapper[lookupType] = response.response;
        return response.response;
      })
    );
  }

  getMSCognitiveServiceConfig(): Observable<MSCognitiveConfigInterface> {
    if (!hasAuthTokens()) {
      return of({} as MSCognitiveConfigInterface);
    }

    if (this.cognitiveServicesConfig) {
      return of(this.cognitiveServicesConfig);
    } else {
      return this.http.get<{ response: MSCognitiveConfigInterface }>(this.BASE_URL + "/ms-config").pipe(
        map(response => response.response),
        tap(data => this.cognitiveServicesConfig = data)
      );
    }
  }

  initSpeechService(): Promise<void> {
    return new Promise((resolve) => {
      if (!hasAuthTokens()) {
        resolve();
        return;
      }

      if (!this.isSpeechServiceInitiated) {
        this.getMSCognitiveServiceConfig().subscribe({
          next: config => {
            SpeechToTextService.init(config);
            this.isSpeechServiceInitiated = true;
            resolve();
          },
          error: () => {
            console.error('Could not fetch speech sdk config');
            resolve();
          },
        })
      } else {
        resolve();
      }
    });
  }
}
